import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useMotionPreferences } from "../hooks/use-motion-preferences";
import "./IndustriesBento.css";

/* 1 XL (2×2) + 2 wide (2×1) + 4 std (1×1) = 12 cells fit a clean 4×3 grid. */
const FEATURED = [
  { slug: "construction-infrastructure", size: "xl" },
  { slug: "hospitality-catering", size: "wide" },
  { slug: "oil-gas-energy", size: "wide" },
  { slug: "healthcare-caregiving", size: "std" },
  { slug: "logistics-warehousing", size: "std" },
  { slug: "manufacturing-industrial", size: "std" },
  { slug: "security-services", size: "std" },
];

function TiltTile({ industry, size, index, enableTilt }) {
  const cardRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 150, damping: 22 });
  const sy = useSpring(my, { stiffness: 150, damping: 22 });
  const rotateX = useTransform(sy, [-0.5, 0.5], ["5deg", "-5deg"]);
  const rotateY = useTransform(sx, [-0.5, 0.5], ["-5deg", "5deg"]);

  const onMove = (e) => {
    if (!enableTilt) return;
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      className={`bento__tile bento__tile--${size}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.55,
        delay: index * 0.06,
        ease: [0.22, 1, 0.36, 1],
      }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={
        enableTilt ? { rotateX, rotateY, transformPerspective: 1200 } : undefined
      }
    >
      <Link
        to={`/industries/${industry.slug}`}
        className="bento__link"
        aria-label={`${industry.name} — view industry details`}
      >
        <div className="bento__media">
          <img
            src={industry.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="bento__image"
          />
        </div>
        <div className="bento__overlay" />
        <div className="bento__content">
          <span className="bento__eyebrow">Industry</span>
          <h3 className="bento__title">{industry.name}</h3>
          <span className="bento__arrow" aria-hidden="true">
            <ArrowUpRight size={18} strokeWidth={2} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function IndustriesBento({ industries }) {
  const { enableHeavyMotion } = useMotionPreferences();
  const byKey = new Map(industries.map((i) => [i.slug, i]));
  const tiles = FEATURED.map((f) => ({
    industry: byKey.get(f.slug),
    size: f.size,
  })).filter((t) => t.industry);

  return (
    <section id="industries" className="bento">
      <div className="container">
        <div className="bento__header">
          <div>
            <span className="eyebrow">What we do</span>
            <h2 className="bento__title-main">
              17 Industries. <span className="bento__title-accent">One Standard.</span>
            </h2>
            <p className="bento__intro">
              Whether you&rsquo;re hiring construction crews or healthcare
              workers, Adel applies the same standard of care to every
              placement.
            </p>
          </div>
          <Link to="/industries" className="bento__see-all">
            See all 17
            <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>

        <div className="bento__grid">
          {tiles.map(({ industry, size }, i) => (
            <TiltTile
              key={industry.slug}
              industry={industry}
              size={size}
              index={i}
              enableTilt={enableHeavyMotion}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
